import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Phone, MessageSquare, Mail, StickyNote, CheckSquare, Home, Sparkles,
  Pencil, MapPin, Calendar, Star, Target, FileSignature, Clock, Link2, Loader2, Plus, Trash2,
  Workflow, Play, Pause,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import api, { apiErr, fileUrl } from "../lib/api";
import { Loading, Avatar, TempBadge, Pill, SectionCard, Empty, Stamp, RecordMeta } from "../components/kit";
import { FormModal, Field, Select as FSelect } from "../components/form";
import ContactForm from "../components/ContactForm";
import { TaskModal, NoteModal, CallModal, SmsModal, EmailModal, OpportunityModal } from "../components/actions";
import { fullName, titleCase, money, RELATIONSHIP_TYPES, isProfessional } from "../lib/constants";
import { VendorProfileCard, CategoryPill } from "../components/VendorProfile";
import LeadScoreBadge from "../components/LeadScoreBadge";
import LastContact from "../components/LastContact";
import { toast } from "sonner";

const COMM_ICON = { call: Phone, sms: MessageSquare, email: Mail };

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [comms, setComms] = useState([]);
  const [rels, setRels] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [opps, setOpps] = useState([]);
  const [files, setFiles] = useState([]);
  const [modal, setModal] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [seqTemplates, setSeqTemplates] = useState([]);

  const load = useCallback(async () => {
    const [cc, tl, nt, cm, rl, tk, op, fl, en, tp] = await Promise.all([
      api.get(`/contacts/${id}`), api.get(`/timeline?contact_id=${id}`), api.get(`/notes?contact_id=${id}`),
      api.get(`/communications?contact_id=${id}`), api.get(`/relationships?contact_id=${id}`),
      api.get(`/tasks?contact_id=${id}`), api.get(`/opportunities?contact_id=${id}`), api.get(`/files?contact_id=${id}`),
      api.get(`/sequences/enrollments?contact_id=${id}`), api.get(`/sequences/templates`),
    ]);
    setC(cc.data); setTimeline(tl.data); setNotes(nt.data); setComms(cm.data);
    setRels(rl.data); setTasks(tk.data.items || []); setOpps(op.data.items || []); setFiles(fl.data);
    setEnrollments(en.data); setSeqTemplates(tp.data);
    setAi(cc.data.ai_summary || null);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runAi = async (kind) => {
    setAiLoading(true);
    try {
      if (kind === "summary") { const { data } = await api.post(`/ai/contact-summary/${id}`); setAi(data); }
      else { const { data } = await api.post(`/ai/followup/${id}`); setAi((p) => ({ ...(p || {}), followup: data })); toast.success("AI follow-up ready"); }
    } catch (e) { toast.error(apiErr(e)); }
    setAiLoading(false);
  };

  if (!c) return <Loading label="Loading contact" />;
  const closeModal = () => { setModal(null); load(); };

  const ACTIONS = [
    { key: "call", label: "Call", icon: Phone }, { key: "sms", label: "SMS", icon: MessageSquare },
    { key: "email", label: "Email", icon: Mail }, { key: "note", label: "Note", icon: StickyNote },
    { key: "task", label: "Task", icon: CheckSquare }, { key: "opportunity", label: "Deal", icon: Target },
  ];

  return (
    <div className="animate-fade-up">
      <button onClick={() => nav("/contacts")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-800 mb-4" data-testid="back-btn"><ArrowLeft className="w-4 h-4" />Contacts</button>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="contact-header">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <Avatar contact={c} size={80} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-extrabold text-navy-800">{fullName(c)}</h1>
              {!isProfessional(c) && <TempBadge value={c.lead_temperature} />}
              <Pill>{titleCase(c.contact_type || "prospect")}</Pill>
              {isProfessional(c) && c.vendor_profile?.primary_category && <CategoryPill testid="contact-vendor-category">{c.vendor_profile.primary_category}</CategoryPill>}
              {c.company && c.company !== fullName(c) && <span className="text-sm text-slate-500" data-testid="contact-company">{c.company}</span>}
              {!isProfessional(c) && <LeadScoreBadge contact={c} size="md" />}
              <LastContact contact={c} testid="header-last-contact" />
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
              {c.primary_phone && <a href={`tel:${c.primary_phone}`} className="flex items-center gap-1.5 hover:text-navy-800"><Phone className="w-3.5 h-3.5" />{c.primary_phone}</a>}
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-navy-800"><Mail className="w-3.5 h-3.5" />{c.email}</a>}
              {(c.city || c.state) && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{[c.city, c.state].filter(Boolean).join(", ")}</span>}
            </div>
            <RecordMeta doc={c} className="mt-2" testid="contact-record-meta" />
          </div>
          <Button variant="outline" onClick={() => setModal("edit")} data-testid="edit-contact-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100">
          {ACTIONS.map((a) => (
            <button key={a.key} onClick={() => setModal(a.key)} data-testid={`action-${a.key}`}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm font-medium text-navy-800 hover:border-gold hover:bg-white transition-[border-color,background-color]">
              <a.icon className="w-4 h-4 text-gold-dark" />{a.label}
            </button>
          ))}
          <button onClick={() => setLinkOpen(true)} data-testid="action-link-property" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm font-medium text-navy-800 hover:border-gold hover:bg-white transition-[border-color,background-color]"><Home className="w-4 h-4 text-gold-dark" />Link Property</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
              {["overview", "timeline", "properties", "tasks", "communications", "opportunities", "documents", "notes"].map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="capitalize data-[state=active]:bg-navy-800 data-[state=active]:text-white">{t}</TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* AI summary */}
              <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-xl p-5 text-white" data-testid="ai-summary-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-gold" /><p className="font-display font-semibold">AI Relationship Summary</p><span className="text-[10px] uppercase tracking-wide bg-gold/20 text-gold px-1.5 py-0.5 rounded">AI generated</span></div>
                  <Button size="sm" onClick={() => runAi("summary")} disabled={aiLoading} className="bg-gold hover:bg-gold-bright text-navy-900 h-7 text-xs" data-testid="generate-summary-btn">{aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Generate"}</Button>
                </div>
                {ai ? (
                  <div className="space-y-2 text-sm text-slate-200">
                    {ai.relationship_summary && <p><span className="text-gold font-semibold">Relationship: </span>{ai.relationship_summary}</p>}
                    {ai.current_situation && <p><span className="text-gold font-semibold">Situation: </span>{ai.current_situation}</p>}
                    {ai.motivation && <p><span className="text-gold font-semibold">Motivation: </span>{ai.motivation}</p>}
                    {ai.next_best_action && <p><span className="text-gold font-semibold">Next best action: </span>{ai.next_best_action}</p>}
                  </div>
                ) : <p className="text-sm text-slate-400">Generate an AI summary grounded strictly in this contact's recorded data.</p>}
                <div className="mt-3 pt-3 border-t border-white/10">
                  <Button size="sm" variant="ghost" onClick={() => runAi("followup")} disabled={aiLoading} className="text-gold hover:text-gold-bright hover:bg-white/5 h-7 text-xs" data-testid="ai-followup-btn"><Sparkles className="w-3 h-3 mr-1" />AI Follow-Up Suggestions</Button>
                  {ai?.followup && (
                    <div className="mt-2 text-sm text-slate-200 space-y-2">
                      {ai.followup.call_talking_points && <div><p className="text-gold text-xs font-semibold">Call points:</p><ul className="list-disc ml-4 text-xs">{ai.followup.call_talking_points.map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
                      {ai.followup.suggested_sms && <p className="text-xs"><span className="text-gold font-semibold">SMS: </span>{ai.followup.suggested_sms}</p>}
                      {ai.followup.next_task && <p className="text-xs"><span className="text-gold font-semibold">Next task: </span>{ai.followup.next_task}</p>}
                    </div>
                  )}
                </div>
              </div>

              {isProfessional(c) && <VendorProfileCard profile={c.vendor_profile} />}
              {c.buyer_profile && Object.keys(c.buyer_profile).length > 0 && (
                <SectionCard title="Buyer Profile">
                  <ProfileGrid data={c.buyer_profile} />
                </SectionCard>
              )}
              {c.seller_profile && Object.keys(c.seller_profile).length > 0 && (
                <SectionCard title="Seller Profile"><ProfileGrid data={c.seller_profile} /></SectionCard>
              )}
              {c.investor_profile && Object.keys(c.investor_profile).length > 0 && (
                <SectionCard title="Investor Profile"><ProfileGrid data={c.investor_profile} /></SectionCard>
              )}
              {c.notes && <SectionCard title="Notes"><p className="text-sm text-slate-600 whitespace-pre-wrap">{c.notes}</p></SectionCard>}
            </TabsContent>

            <TabsContent value="timeline" className="mt-4">
              <SectionCard title="Activity Timeline" testid="timeline-list">
                {timeline.length === 0 ? <Empty title="No activity yet" /> : (
                  <div className="relative pl-6">
                    <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" />
                    {timeline.map((a) => (
                      <div key={a.id} className="relative pb-5 last:pb-0">
                        <div className="absolute -left-[18px] top-0.5 w-3 h-3 rounded-full bg-gold ring-4 ring-white" />
                        <p className="text-sm font-medium text-navy-800">{a.title}</p>
                        {a.description && <p className="text-xs text-slate-500 mt-0.5">{a.description}</p>}
                        <Stamp by={a.author || "System"} at={a.timestamp} className="mt-1" testid={`timeline-stamp-${a.id}`} />
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </TabsContent>

            <TabsContent value="properties" className="mt-4">
              <SectionCard title="Linked Properties" action={<Button size="sm" variant="outline" onClick={() => setLinkOpen(true)}><Plus className="w-3.5 h-3.5 mr-1" />Link</Button>}>
                {rels.filter((r) => r.property).length === 0 ? <Empty title="No linked properties" /> : (
                  <div className="space-y-2">
                    {rels.filter((r) => r.property).map((r) => (
                      <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-gold/40 cursor-pointer" onClick={() => nav(`/properties/${r.property_id}`)}>
                        <Home className="w-4 h-4 text-gold-dark" />
                        <div className="flex-1"><p className="text-sm font-medium text-navy-800">{r.property.address}</p><p className="text-xs text-slate-400">{r.property.city} · {money(r.property.asking_price)}</p><Stamp prefix="Linked by" by={r.created_by_name || "System"} at={r.created_at} className="mt-1" /></div>
                        <Pill>{titleCase(r.relationship_type)}</Pill>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </TabsContent>

            <TabsContent value="tasks" className="mt-4">
              <SectionCard title="Tasks & Follow-Ups" action={<Button size="sm" variant="outline" onClick={() => setModal("task")}><Plus className="w-3.5 h-3.5 mr-1" />Task</Button>}>
                {tasks.length === 0 ? <Empty title="No tasks" /> : <div className="space-y-2">{tasks.map((t) => <TaskRow key={t.id} t={t} onDone={load} />)}</div>}
              </SectionCard>
            </TabsContent>

            <TabsContent value="communications" className="mt-4">
              <SectionCard title="Communications">
                {comms.length === 0 ? <Empty title="No communications" /> : (
                  <div className="space-y-3">
                    {comms.map((m) => {
                      const Icon = COMM_ICON[m.type] || MessageSquare;
                      return (
                        <div key={m.id} className="flex gap-3 p-3 rounded-lg border border-slate-100">
                          <div className="w-8 h-8 rounded-lg bg-navy-800/5 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-navy-700" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between"><p className="text-sm font-medium text-navy-800">{m.summary || titleCase(m.type)}</p><span className="text-xs text-slate-400">{new Date(m.timestamp).toLocaleDateString()}</span></div>
                            {m.subject && <p className="text-xs font-medium text-slate-600">{m.subject}</p>}
                            {m.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{m.body}</p>}
                            {m.status && <span className="text-[10px] uppercase tracking-wide text-slate-400">{m.status}{m.provider_message ? ` · ${m.provider_message}` : ""}</span>}
                            <div className="mt-1"><Stamp prefix="Logged by" by={m.created_by_name || "System"} at={m.timestamp} testid={`comm-stamp-${m.id}`} /></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>
            </TabsContent>

            <TabsContent value="opportunities" className="mt-4">
              <SectionCard title="Opportunities" action={<Button size="sm" variant="outline" onClick={() => setModal("opportunity")}><Plus className="w-3.5 h-3.5 mr-1" />Deal</Button>}>
                {opps.length === 0 ? <Empty title="No opportunities" /> : <div className="space-y-2">{opps.map((o) => (
                  <div key={o.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 cursor-pointer hover:border-gold/40" onClick={() => nav("/pipeline")}>
                    <Target className="w-4 h-4 text-gold-dark" /><div className="flex-1"><p className="text-sm font-medium text-navy-800">{o.name}</p><p className="text-xs text-slate-400">{money(o.estimated_value)} · {o.probability}%</p><RecordMeta doc={o} className="mt-1" testid={`opp-meta-${o.id}`} /></div><Pill>{o.stage}</Pill>
                  </div>
                ))}</div>}
              </SectionCard>
            </TabsContent>

            <TabsContent value="documents" className="mt-4">
              <DocsTab contactId={id} files={files} onSaved={load} />
            </TabsContent>

            <TabsContent value="notes" className="mt-4">
              <SectionCard title="Notes" action={<Button size="sm" variant="outline" onClick={() => setModal("note")}><Plus className="w-3.5 h-3.5 mr-1" />Note</Button>}>
                {notes.length === 0 ? <Empty title="No notes" /> : <div className="space-y-3">{notes.map((n) => (
                  <div key={n.id} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-sm text-navy-800 whitespace-pre-wrap">{n.text}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                      <Stamp by={n.author} at={n.timestamp} testid={`note-stamp-${n.id}`} />
                      {n.edited && <Stamp prefix="Edited by" by={n.updated_by_name || n.edit_history?.[n.edit_history.length - 1]?.editor} at={n.updated_at} icon={Pencil} />}
                    </div>
                  </div>
                ))}</div>}
              </SectionCard>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          <SectionCard title="Details">
            <dl className="space-y-3 text-sm">
              <Row icon={Calendar} label="Next follow-up" value={c.next_follow_up || "—"} />
              <Row icon={Clock} label="Last contacted" value={c.last_contacted ? <LastContact contact={c} testid="detail-last-contact" /> : "—"} />
              <Row icon={Star} label="Lead source" value={c.lead_source || "—"} />
              <Row icon={Target} label="Lead status" value={c.lead_status || "—"} />
              <Row icon={FileSignature} label="Created by" value={c.created_by_name || "System"} />
              <Row icon={Clock} label="Created" value={c.created_at ? new Date(c.created_at).toLocaleString() : "—"} />
              <Row icon={Pencil} label="Updated by" value={c.updated_by_name || c.created_by_name || "—"} />
              <Row icon={Clock} label="Updated" value={c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"} />
            </dl>
          </SectionCard>
          {c.tags?.length > 0 && (
            <SectionCard title="Tags"><div className="flex flex-wrap gap-2">{c.tags.map((t) => <Pill key={t}>{t}</Pill>)}</div></SectionCard>
          )}
          <AutomationCard contactId={id} enrollments={enrollments} templates={seqTemplates} onChange={load} />
          {c.verification_status && (
            <SectionCard title="Prospecting"><div className="space-y-2 text-sm"><Row icon={Link2} label="Verification" value={titleCase(c.verification_status)} /><Row icon={Home} label="Source" value={c.listing_source || c.import_source || "—"} /></div></SectionCard>
          )}
        </div>
      </div>

      <ContactForm open={modal === "edit"} onClose={closeModal} contact={c} onSaved={load} />
      <TaskModal open={modal === "task"} onClose={closeModal} contactId={id} onSaved={load} />
      <NoteModal open={modal === "note"} onClose={closeModal} contactId={id} onSaved={load} />
      <CallModal open={modal === "call"} onClose={closeModal} contactId={id} contactPhone={c.primary_phone} onSaved={load} />
      <SmsModal open={modal === "sms"} onClose={closeModal} contactId={id} contactPhone={c.primary_phone || c.mobile_phone} onSaved={load} prefill={ai?.followup?.suggested_sms} />
      <EmailModal open={modal === "email"} onClose={closeModal} contactId={id} contactEmail={c.email} onSaved={load} prefill={ai?.followup?.suggested_email} />
      <OpportunityModal open={modal === "opportunity"} onClose={closeModal} contactId={id} onSaved={load} />
      <LinkPropertyModal open={linkOpen} onClose={() => { setLinkOpen(false); load(); }} contactId={id} />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      <span className="text-slate-500 flex-1">{label}</span>
      <span className="font-medium text-navy-800 text-right">{value}</span>
    </div>
  );
}

function ProfileGrid({ data }) {
  const entries = Object.entries(data).filter(([, v]) => v !== "" && v != null);
  return (
    <div className="grid grid-cols-2 gap-3 text-sm">
      {entries.map(([k, v]) => (
        <div key={k}><p className="text-xs text-slate-400">{titleCase(k)}</p><p className="text-navy-800 font-medium">{typeof v === "boolean" ? (v ? "Yes" : "No") : (typeof v === "number" && k.includes("price") ? money(v) : String(v))}</p></div>
      ))}
    </div>
  );
}

function TaskRow({ t, onDone }) {
  const complete = async () => { await api.put(`/tasks/${t.id}`, { status: "completed", completion_date: new Date().toISOString() }); toast.success("Task completed"); onDone(); };
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-100">
      <button onClick={complete} disabled={t.status === "completed"} className={`w-5 h-5 rounded border-2 flex items-center justify-center ${t.status === "completed" ? "bg-emerald-500 border-emerald-500" : "border-slate-300 hover:border-gold"}`} data-testid={`complete-task-${t.id}`}>
        {t.status === "completed" && <CheckSquare className="w-3 h-3 text-white" />}
      </button>
      <div className="flex-1"><p className={`text-sm ${t.status === "completed" ? "line-through text-slate-400" : "text-navy-800"}`}>{t.title}</p><p className="text-xs text-slate-400">{t.task_type} · due {t.due_date}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
          <Stamp prefix={t.automated ? "Auto-created for" : "Created by"} by={t.created_by_name || "System"} at={t.created_at} testid={`task-stamp-${t.id}`} />
          {t.status === "completed" && <Stamp prefix="Completed by" by={t.completed_by_name || t.updated_by_name} at={t.completed_at || t.updated_at} icon={CheckSquare} />}
        </div></div>
      <Pill className={t.priority === "urgent" ? "bg-red-50 text-red-600 border-red-200" : ""}>{t.priority}</Pill>
    </div>
  );
}

function DocsTab({ contactId, files, onSaved }) {
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("contact_id", contactId); fd.append("category", "Correspondence");
    try { await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); onSaved(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <SectionCard title="Documents" action={<label className="text-xs font-semibold text-gold-dark cursor-pointer hover:underline">Upload<input type="file" className="hidden" onChange={upload} data-testid="upload-contact-doc" /></label>}>
      {files.length === 0 ? <Empty title="No documents" /> : (
        <div className="space-y-2">{files.map((f) => (
          <a key={f.id} href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-gold/40">
            {f.is_image ? <img src={fileUrl(f.id)} alt="" className="w-10 h-10 rounded object-cover" /> : <FileSignature className="w-5 h-5 text-slate-400" />}
            <div className="flex-1"><p className="text-sm text-navy-800">{f.original_filename}</p><p className="text-xs text-slate-400">{f.category}</p><Stamp prefix="Uploaded by" by={f.uploaded_by_name || "System"} at={f.created_at} className="mt-1" testid={`doc-stamp-${f.id}`} /></div>
          </a>
        ))}</div>
      )}
    </SectionCard>
  );
}

function AutomationCard({ contactId, enrollments, templates, onChange }) {
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const active = enrollments.filter((e) => !e.completed);
  const enroll = async () => {
    if (!sel) { toast.error("Choose a sequence"); return; }
    setBusy(true);
    try { await api.post("/sequences/enroll", { contact_id: contactId, sequence_key: sel }); toast.success("Sequence started — first task created"); setSel(""); onChange(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const toggle = async (e) => { await api.post(`/sequences/${e.id}/toggle`); toast.success(e.active ? "Sequence paused" : "Sequence resumed"); onChange(); };
  const remove = async (e) => { await api.delete(`/sequences/${e.id}`); toast.success("Removed from sequence"); onChange(); };

  return (
    <SectionCard title="Follow-Up Automation" testid="automation-card">
      {active.length > 0 && (
        <div className="space-y-2 mb-4">
          {active.map((e) => {
            const total = e.steps?.length || 0;
            const done = Math.min(e.next_step_index || 0, total);
            return (
              <div key={e.id} className="rounded-lg border border-slate-100 p-3" data-testid={`enrollment-${e.id}`}>
                <div className="flex items-center gap-2">
                  <Workflow className="w-4 h-4 text-gold-dark" />
                  <p className="text-sm font-medium text-navy-800 flex-1">{e.sequence_name}</p>
                  <button onClick={() => toggle(e)} data-testid={`toggle-seq-${e.id}`} className="text-slate-400 hover:text-navy-800">{e.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
                  <button onClick={() => remove(e)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-2"><div className="flex justify-between text-[11px] text-slate-400 mb-1"><span>{e.active ? "Active" : "Paused"} · step {Math.min(done + 1, total)} of {total}</span><span>{done}/{total}</span></div>
                  <div className="h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-gold to-gold-dark" style={{ width: `${(done / total) * 100}%` }} /></div></div>
                <p className="text-[11px] text-slate-400 mt-1.5">Completing each task auto-creates the next follow-up.</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                  <Stamp prefix="Enrolled by" by={e.enrolled_by_name || e.created_by_name || "System"} at={e.enrolled_at} testid={`enrollment-stamp-${e.id}`} />
                  {e.updated_by_name && e.updated_at !== e.created_at && <Stamp prefix="Updated by" by={e.updated_by_name} at={e.updated_at} icon={Pencil} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex gap-2">
        <FSelect options={templates.map((t) => ({ value: t.key, label: t.name }))} value={sel} onChange={setSel} placeholder="Choose a sequence…" testid="sequence-select" />
        <Button onClick={enroll} disabled={busy} className="bg-navy-800 text-white shrink-0" data-testid="enroll-sequence-btn"><Plus className="w-4 h-4" /></Button>
      </div>
    </SectionCard>
  );
}

function LinkPropertyModal({ open, onClose, contactId }) {
  const [props, setProps] = useState([]);
  const [f, setF] = useState({ relationship_type: "interested_buyer" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) api.get("/properties?limit=500").then(({ data }) => setProps(data.items)); }, [open]);
  const submit = async () => {
    if (!f.property_id) { toast.error("Select a property"); return; }
    setLoading(true);
    try { await api.post("/relationships", { contact_id: contactId, ...f }); toast.success("Property linked"); onClose(); }
    catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title="Link Property" onSubmit={submit} loading={loading} testid="link-property-modal">
      <Field label="Property"><FSelect options={props.map((p) => ({ value: p.id, label: `${p.address} · ${p.city}` }))} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v })} placeholder="Select property…" testid="link-property-select" /></Field>
      <Field label="Relationship"><FSelect options={RELATIONSHIP_TYPES} value={f.relationship_type} onChange={(v) => setF({ ...f, relationship_type: v })} /></Field>
    </FormModal>
  );
}
